import { useState, useEffect } from 'react';
import { Phone, Mail, User } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/Dialog';
import { toast } from '../ui/Toast';
import { api, type Listing, type CreateLeadInput } from '../../services/api';

interface SaveAsLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listing: Listing | null;
  searchId?: string;
  onLeadCreated: () => void;
}

export function SaveAsLeadDialog({
  open,
  onOpenChange,
  listing,
  searchId,
  onLeadCreated,
}: SaveAsLeadDialogProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Pre-fill contact fields from listing data when dialog opens
  useEffect(() => {
    if (!open || !listing) return;

    // Use contact data already available in the listing (from search results)
    setName(listing.contactName || listing.agencyName || '');
    setPhone(listing.contactPhone || '');
    setEmail(listing.contactEmail || '');
  }, [open, listing]);

  const handleSave = async () => {
    if (!listing || !name.trim()) return;

    setIsSaving(true);
    try {
      const data: CreateLeadInput = {
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        propertyAddress: listing.address || undefined,
        sourceUrl: listing.sourceUrl,
        platform: listing.platform,
        externalListingId: listing.externalId,
        listingTitle: listing.title,
        listingPrice: listing.price,
        listingImage: getFirstImageUrl(listing) || undefined,
        savedSearchId: searchId,
      };

      await api.createLead(data);
      toast({ title: 'Lead salvato', variant: 'success' });
      onOpenChange(false);
      onLeadCreated();
    } catch (err: any) {
      if (err?.status === 409) {
        toast({ title: 'Lead gia esistente per questo annuncio', variant: 'error' });
      } else {
        toast({ title: 'Errore nel salvataggio', variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>Salva come Lead</DialogTitle>
        </DialogHeader>

        {listing && (
          <div className="py-2">
            {/* Listing reference */}
            <div className="flex gap-3 p-3 bg-gray-50 rounded-xl mb-4">
              {getFirstImageUrl(listing) ? (
                <img
                  src={getFirstImageUrl(listing)!}
                  alt=""
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-gray-200 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{listing.title}</p>
                {listing.price != null && (
                  <p className="text-sm font-semibold text-blue-600">
                    {listing.price.toLocaleString('it-IT')} EUR
                    {listing.contractType === 'RENT' ? '/mese' : ''}
                  </p>
                )}
                {listing.address && (
                  <p className="text-xs text-gray-400 truncate">{listing.address}</p>
                )}
              </div>
            </div>

            {/* Contact fields */}
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <User className="h-3.5 w-3.5" />
                  Nome / Agenzia *
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome proprietario o agenzia"
                  className="h-11"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5" />
                  Telefono
                </label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+39 333 1234567"
                  type="tel"
                  className="h-11"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </label>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@esempio.it"
                  type="email"
                  className="h-11"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
          >
            {isSaving ? 'Salvataggio...' : 'Salva Lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function getFirstImageUrl(listing: Listing): string | null {
  if (!listing.images || listing.images.length === 0) return null;
  const first = listing.images[0];
  if (typeof first === 'string') return first;
  return (first as { url: string }).url || null;
}
